import React, { useState, useEffect } from 'react';
import Nav from './Nav';
import SideBar from './SideBar';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';
import Home from './Pages/Home';
import DemoPage from './Pages/DemoPage';
import ConvertDataType from './Pages/ConvertDataType';
import TextSizeChecker from './Pages/TextSizeChecker';
import TextDiffChecker from './Pages/TextDiffChecker';
import UnitConverter from './Pages/UnitConverter';
import CronJobParser from './Pages/CronJobParser';
import JsonQuery from './Pages/JsonQuery';
import ExcelJson from './Pages/ExcelJson';
import ColorPicker from './Pages/ColorPicker';
import RegexTester from './Pages/RegexTester';
import Base64Tool from './Pages/Base64';
import CsvPreview from './Pages/CsvPreview';
import QrCodeGenerator from './Pages/QrCodeGenerator';
import FaceBookTextPage from './Pages/FaceBookText';
import FancyTextPage from './Pages/FancyText'
import DBMLTransformPage from './Pages/DBMLTransform';
import JsonViewPage from './Pages/JsonView';
import CaseConverter from './Pages/CaseConverter';
import LoremIpsum from './Pages/LoremIpsum';
import SQLFormatPage from './Pages/SqlFormat';
import QrCodeScannerPage from './Pages/QrCodeScanner';
import CodeToImagePage from './Pages/CodeToImage';
import RemoveBackgroundImagePage from './Pages/RemoveBackgroundImage';
import SlugUrlGenerator from './Pages/SlugUrlGenerator';
import PasswordGenerator from './Pages/PasswordGenerator'
import BarcodeGenerator from './Pages/BarcodeGenerator'
import ExtractSeoFromUrl from './Pages/ExtractSeoFromUrl';
import FbDownload from './Pages/FbDownload';
import CodeDiffChecker from './Pages/CodeDiffChecker';
import AsciiArtTextGenerator from './Pages/AsciiArtTextGenerator';
import TextToSocialImage from './Pages/TextToSocialImage';
import TweetGenerator from './Pages/TweetGenerator';
import RemoveBackground from './Pages/Images/RemoveBackground';
import JsonVisualizer from './Pages/JsonTreeView';

import BubbleButton from './components/BubbleButton';
import ReportIssueModal from './components/ReportIssueModal';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import appConfig from './appConfig';
const { userSettings } = appConfig;

function App() {
  const [editorTheme, setEditorTime] = useState(userSettings.editorTheme)
  const [appTheme, setAppTheme] = useState(userSettings.colorTheme)

  useEffect(() => {
    const themeToggleDarkIcon = document.getElementById('theme-toggle-dark-icon');
    const themeToggleLightIcon = document.getElementById('theme-toggle-light-icon');

    // Ensure the icons are displayed correctly based on the theme
    if (localStorage.getItem('color-theme') === 'dark' ||
       (!('color-theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      themeToggleLightIcon?.classList.remove('hidden');
    } else {
      themeToggleDarkIcon?.classList.remove('hidden');
    }

    const themeToggleBtn = document.getElementById('theme-toggle');

    const toggleTheme = () => {
      // Toggle the icons
      themeToggleDarkIcon?.classList.toggle('hidden');
      themeToggleLightIcon?.classList.toggle('hidden');

      // Toggle the theme
      if (localStorage.getItem('color-theme')) {
        if (localStorage.getItem('color-theme') === 'light') {
          document.documentElement.classList.add('dark');
          localStorage.setItem('color-theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('color-theme', 'light');
        }
      } else {
        if (document.documentElement.classList.contains('dark')) {
          document.documentElement.classList.remove('dark');
          localStorage.setItem('color-theme', 'light');
        } else {
            document.documentElement.classList.add('dark');
            localStorage.setItem('color-theme', 'dark');
        }
      }

      document.dispatchEvent(new Event('dark-mode'));
      const colorTheme = localStorage.getItem('color-theme');
      const editorTheme = `vs-${colorTheme}`
      setEditorTime(editorTheme)
      setAppTheme(colorTheme)
    };

    themeToggleBtn?.addEventListener('click', toggleTheme);

    return () => {
      themeToggleBtn?.removeEventListener('click', toggleTheme);
    };
  }, []);

  return (
    <>
      <Nav />
      <div className="min-h-screen flex pt-16 overflow-hidden bg-gray-50 dark:bg-gray-900">
        <SideBar appTheme={appTheme} />
        <div className="fixed inset-0 z-10 hidden bg-gray-900/50 dark:bg-gray-900/90" id="sidebarBackdrop"></div>
        <div id="main-content" className="relative w-full h-full overflow-y-auto bg-gray-50 lg:ml-64 dark:bg-gray-900">
          <main>
            <div className="grid grid-cols-1 px-4 pt-6 xl:grid-cols-3 xl:gap-4 dark:bg-gray-900">
              <Breadcrumb appTheme={appTheme} />
              <div className="col-span-full">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/app" element={<Home />} />
                  <Route path="/demo" element={<DemoPage />} />
                  <Route path="/text-size-calculator" element={<TextSizeChecker editorTheme={editorTheme} />} />
                  <Route path="/text-diff-checker" element={<TextDiffChecker editorTheme={editorTheme} />} />
                  <Route path="/yaml-2-json" element={<ConvertDataType editorTheme={editorTheme} convertName="YAML:JSON" />} />
                  <Route path="/json-2-yaml" element={<ConvertDataType editorTheme={editorTheme} convertName="JSON:YAML" />} />
                  <Route path="/unit-converter" element={<UnitConverter appTheme={appTheme} />} />
                  <Route path="/cron-job-parser" element={<CronJobParser />} />
                  <Route path="/json-query" element={<JsonQuery editorTheme={editorTheme} />} />
                  <Route path="/object-2-json" element={<ConvertDataType editorTheme={editorTheme} convertName="OBJECT:JSON" />} />
                  <Route path="/csv-2-json" element={<ConvertDataType editorTheme={editorTheme} convertName="CSV:JSON" />} />
                  <Route path="/json-2-csv" element={<ConvertDataType editorTheme={editorTheme} convertName="JSON:CSV" />} />
                  <Route path="/excel-2-json" element={<ExcelJson editorTheme={editorTheme} />} />
                  <Route path="/beautiful-json" element={<ConvertDataType editorTheme={editorTheme} convertName="JSON:JSON" />} />
                  <Route path="/minifier-json" element={<ConvertDataType editorTheme={editorTheme} convertName="JSON:JSON MINIFIER" />} />
                  <Route path="/color-picker" element={<ColorPicker />} />
                  <Route path="/regex-tester" element={<RegexTester appTheme={appTheme} editorTheme={editorTheme} />} />
                  <Route path="/base64" element={<Base64Tool editorTheme={editorTheme} />} />
                  <Route path="/csv-preview" element={<CsvPreview editorTheme={editorTheme} />} />
                  <Route path="/qr-code-generator" element={<QrCodeGenerator />} />
                  <Route path="/facebook-text" element={<FaceBookTextPage />} />
                  <Route path="/fancy-text" element={<FancyTextPage appTheme={appTheme} />} />
                  <Route path="/schema-transform" element={<DBMLTransformPage appTheme={appTheme} editorTheme={editorTheme} />} />
                  <Route path="/url-encode" element={<ConvertDataType editorTheme={editorTheme} convertName="URL:ENCODED URL" />} />
                  <Route path="/url-decode" element={<ConvertDataType editorTheme={editorTheme} convertName="ENCODED URL:URL" />} />
                  <Route path="/json-tree-viewer" element={<JsonViewPage editorTheme={editorTheme} />} />
                  <Route path="/case-converter" element={<CaseConverter editorTheme={editorTheme} />} />
                  <Route path="/lorem-ipsum-generator" element={<LoremIpsum editorTheme={editorTheme} />} />
                  <Route path="/random-person" element={<LoremIpsum randomType='randomPerson' editorTheme={editorTheme} />} />
                  <Route path="/sql-formater" element={<SQLFormatPage appTheme={appTheme} editorTheme={editorTheme} />} />
                  <Route path="/qrcode-reader" element={<QrCodeScannerPage editorTheme={editorTheme} />} />
                  <Route path="/code-to-image" element={<CodeToImagePage appTheme={appTheme} editorTheme={editorTheme} />} />
                  <Route path="/remove-background-image" element={<RemoveBackgroundImagePage editorTheme={editorTheme} />} />
                  <Route path="/url-slug-generator" element={<SlugUrlGenerator editorTheme={editorTheme} />} />
                  <Route path="/password-generator" element={<PasswordGenerator editorTheme={editorTheme} />} />
                  <Route path="/barcode-generator" element={<BarcodeGenerator editorTheme={editorTheme} />} />
                  <Route path="/extract-seo-from-url" element={<ExtractSeoFromUrl editorTheme={editorTheme} />} />
                  <Route path="/fb-download" element={<FbDownload editorTheme={editorTheme} />} />
                  <Route path="/json-diff-checker" element={<CodeDiffChecker compareLanguage='json' editorTheme={editorTheme} />} />
                  <Route path="/code-diff-checker" element={<CodeDiffChecker appTheme={appTheme} editorTheme={editorTheme} />} />
                  <Route path="/ascii-art-text-generator" element={<AsciiArtTextGenerator appTheme={appTheme} editorTheme={editorTheme} />} />
                  <Route path="/text-to-tweet-image" element={<TextToSocialImage editorTheme={editorTheme} />} />
                  <Route path="/tweet-generator" element={<TweetGenerator editorTheme={editorTheme} />} />
                  <Route path="/remove-background" element={<RemoveBackground editorTheme={editorTheme} />} />
                  <Route path="/json-visualizer" element={<JsonVisualizer editorTheme={editorTheme} />} />
                </Routes>
              </div>
            </div>
          </main>
          <Footer />
          <BubbleButton />
          <ReportIssueModal />
          <ToastContainer />
          <p class="my-10 text-sm text-center text-gray-500">
              &copy; 2024 <a href="https://TOOlover.pro/" class="hover:underline" target="_blank">TOOlover</a>. All rights reserved.
          </p>
        </div>
      </div>
    </>
  )
}

export default App;
